import React, {
  type FC,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Flex, type UseDisclosureProps, useDisclosure } from '@chakra-ui/react';

import { Viewport } from '@/components/Viewport';
import { MediaDeviceProvider } from '@/features/media';
import useDisableRightClickAndShortcuts from '@/utils/hooks/useBlockRightClick';

import { SurveyNavBar } from './SurveyNavBar';

export type SurveyLayoutContextNavDisplayed = boolean;

type SurveyLayoutContextValue = {
  navDisplayed: SurveyLayoutContextNavDisplayed;
  setNavDisplayed: React.Dispatch<
    React.SetStateAction<SurveyLayoutContextNavDisplayed>
  >;
  navDrawer: UseDisclosureProps;
  isPublicOnly: boolean;
  disableLangChange: boolean;
  setDisableLangChange: React.Dispatch<React.SetStateAction<boolean>>;
};

export const SurveyLayoutContext =
  React.createContext<SurveyLayoutContextValue | null>(null);

export const useSurveyLayoutContext = () => {
  const ctx = useContext(SurveyLayoutContext);
  if (ctx === null) {
    throw new Error('Missing parent <SurveyLayout> component');
  }
  return ctx;
};

export const useSurveyLayoutHideNav = (
  displayed: SurveyLayoutContextNavDisplayed = true
) => {
  const { setNavDisplayed } = useSurveyLayoutContext();

  useEffect(() => {
    setNavDisplayed(displayed);
    return () => setNavDisplayed(true);
  }, [setNavDisplayed, displayed]);
};

export const SurveyLayout: FC<
  React.PropsWithChildren<{
    isPublicOnly?: boolean;
  }>
> = ({ isPublicOnly = false, children }) => {
  const [navDisplayed, setNavDisplayed] =
    useState<SurveyLayoutContextNavDisplayed>(true);
  const [disableLangChange, setDisableLangChange] = useState<boolean>(false);
  const navDrawer = useDisclosure();
  useDisableRightClickAndShortcuts();

  const providerValue = useMemo(
    () =>
      ({
        navDisplayed,
        setNavDisplayed,
        navDrawer,
        isPublicOnly,
        disableLangChange,
        setDisableLangChange,
      }) satisfies SurveyLayoutContextValue,
    [navDisplayed, navDrawer, isPublicOnly, disableLangChange]
  );

  return (
    <SurveyLayoutContext.Provider value={providerValue}>
      <Viewport bgGradient="linear(to-b, brand.25, gray.50)">
        {!!navDisplayed && <SurveyNavBar display="block" />}
        <Flex flex="1" direction="column">
          <MediaDeviceProvider>{children}</MediaDeviceProvider>
        </Flex>
      </Viewport>
    </SurveyLayoutContext.Provider>
  );
};
